import { TrendingUp } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"
import type {
  CrewAnalyticsWindow,
  CrewEngagement,
} from "@/types/crew-analytics"

/**
 * OKS-65 — 5-bucket engagement distribution histogram.
 *
 * Renders a vertical Recharts `BarChart` with one bar per
 * `ENGAGEMENT_BUCKETS` label. The bucket label, min, max, and count
 * all travel inside the payload (TRD K8.2 self-describing shape) so
 * the tooltip can render the range without referring to a TS-side
 * constant. Open-ended viral bucket: `max === null`; tooltip renders
 * "{min}+" instead of "{min}–{max}".
 *
 * Cohort = active members with `social_account.status='active'` AND
 * ≥1 in-window snapshot; rest of the active cohort drives the
 * "{N} of {M} haven't connected TikTok" footer caption.
 *
 * # Empty branches (FE-derived from prop shape — TRD K11)
 *
 *   - `total_oauth === 0` AND `excluded_count > 0`         → no OAuth empty card
 *   - `total_oauth === 0` AND `no_snapshots_in_window`     → "try All-time" empty card
 *
 * # A11y
 *
 * `role="img"` + `aria-label` summary lists every bucket + the
 * exclude count (UX Spec §6 lock). Sr-only mirror `<table>` mirrors
 * the chart for SR landmark nav.
 */
interface CrewEngagementHistogramProps {
  engagement: CrewEngagement
  windowLabel: CrewAnalyticsWindow
}

export function CrewEngagementHistogram({
  engagement,
  windowLabel,
}: CrewEngagementHistogramProps) {
  const { t } = useTranslation()

  const {
    buckets,
    excluded_count,
    total_oauth,
    total_active,
    no_snapshots_in_window,
  } = engagement

  // Empty branches (the section card still renders for a11y landmark).
  if (total_oauth === 0 && no_snapshots_in_window) {
    return (
      <Card data-testid="crew-analytics-engagement-no-snapshots">
        <CardHeader>
          <CardTitle>{t("analytics.crew.engagement.title")}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
          <TrendingUp
            className="text-muted-foreground h-8 w-8"
            aria-hidden="true"
          />
          <p className="text-muted-foreground text-sm">
            {t("analytics.crew.engagement.empty.no_snapshots_in_window")}
          </p>
        </CardContent>
      </Card>
    )
  }

  if (total_oauth === 0) {
    return (
      <Card data-testid="crew-analytics-engagement-empty">
        <CardHeader>
          <CardTitle>{t("analytics.crew.engagement.title")}</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-3 p-8 text-center">
          <TrendingUp
            className="text-muted-foreground h-8 w-8"
            aria-hidden="true"
          />
          <p className="text-muted-foreground text-sm">
            {t("analytics.crew.engagement.empty.no_oauth")}
          </p>
        </CardContent>
      </Card>
    )
  }

  const chartConfig = {
    count: {
      label: t("analytics.crew.engagement.count_label"),
      color: "var(--chart-1)",
    },
  } satisfies ChartConfig

  // Tooltip payload: each bar carries the localized bucket label,
  // range label, and count. Recharts passes `payload[0].payload` =
  // the data row, so we can reach `label`/`min`/`max` directly.
  const data = buckets.map((b) => ({
    bucketLabel: t(`analytics.crew.engagement.bucket.${b.label}`),
    rangeLabel:
      b.max === null
        ? t("analytics.crew.engagement.range_open", { min: b.min })
        : t("analytics.crew.engagement.range_closed", {
            min: b.min,
            max: b.max,
          }),
    count: b.count,
  }))

  const ariaSummary = t("analytics.crew.engagement.aria_summary", {
    window: t(`analytics.crew.window.${windowLabel}`),
    starting: buckets[0].count,
    developing: buckets[1].count,
    engaged: buckets[2].count,
    high: buckets[3].count,
    viral: buckets[4].count,
    excluded: excluded_count,
    total: total_active,
  })

  return (
    <Card data-testid="crew-engagement-histogram">
      <CardHeader>
        <CardTitle>{t("analytics.crew.engagement.title")}</CardTitle>
        <p className="text-muted-foreground text-sm">
          {t("analytics.crew.engagement.subtitle", {
            window: t(`analytics.crew.window.${windowLabel}`),
          })}
        </p>
      </CardHeader>
      <CardContent>
        <div
          role="img"
          aria-label={ariaSummary}
          data-testid="crew-engagement-histogram-canvas"
        >
          <ChartContainer
            config={chartConfig}
            className="aspect-auto h-[180px] w-full sm:h-[200px] lg:h-[220px]"
          >
            <BarChart
              data={data}
              margin={{ left: 4, right: 12, top: 8, bottom: 0 }}
            >
              <CartesianGrid vertical={false} strokeDasharray="3 3" />
              <XAxis
                dataKey="bucketLabel"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                allowDecimals={false}
                domain={[0, "auto"]}
                width={32}
              />
              <ChartTooltip
                content={<ChartTooltipContent indicator="line" />}
              />
              <Bar
                dataKey="count"
                fill="var(--color-count)"
                radius={[6, 6, 0, 0]}
                isAnimationActive={false}
              />
            </BarChart>
          </ChartContainer>
        </div>

        {/* Sr-only mirror table — SR landmark nav per UX Spec §6. */}
        <table className="sr-only" aria-hidden="false">
          <caption>{t("analytics.crew.engagement.sr_caption")}</caption>
          <thead>
            <tr>
              <th>{t("analytics.crew.engagement.sr_columns.bucket")}</th>
              <th>{t("analytics.crew.engagement.sr_columns.range")}</th>
              <th>{t("analytics.crew.engagement.sr_columns.count")}</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={buckets[i].label}>
                <td>{row.bucketLabel}</td>
                <td>{row.rangeLabel}</td>
                <td>{row.count}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <p className="text-muted-foreground mt-3 text-xs">
          {t("analytics.crew.engagement.footer", {
            excluded: excluded_count,
            total: total_active,
          })}
        </p>
      </CardContent>
    </Card>
  )
}
